import React from "react";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FeatherIcon from "feather-icons-react";
import { DraggableDialog } from "../../consts.js";
import { AddCampaignTrackerSchema } from "./validator.js";
import { Formik } from "formik";
import CreateCampaignStepper from "./create-campaign-header-stepper.jsx";
import Utils from "../../utils.js";
import { useSnackbar } from "../../components/toast/index.jsx";
import { Box, CircularProgress, DialogActions, FormHelperText, IconButton } from "@mui/material";
import { CAMPAIGN_CREATED_FAILED, CAMPAIGN_CREATED_SUCCESS } from "../../helpers/messages.js";
import { getComparatorSort } from "../../helpers/index.js";
import _ from "lodash";

const CreateCampaignStepTwo = ({ currentNonAdminGroup, open, onClose, data, allCampaigns }) => {
    const [trackers, setTrackers] = React.useState([]);
    const [defaultTrackers, setDefaultTrackers] = React.useState([]);
    const [allTrackersSelected, setAllTrackersSelected] = React.useState(false);
    const showSnackbar = useSnackbar();

    function calculateDefaultTrackers(trackers) {
        const _defaultTrackers = [];
        if (trackers && trackers.length > 0) {
            for (var i = 0; i < trackers.length; i++) {
                if (trackers[i].name.includes("-detect")) {
                    _defaultTrackers.push(trackers[i]);
                }
            }
        }
        return _defaultTrackers;
    }

    React.useEffect(() => {
        if (data) {
            const _defaultTrackers = calculateDefaultTrackers(data.trackers);
            setDefaultTrackers(_defaultTrackers);
            const sortedTrackers = [...data.trackers].sort(getComparatorSort('desc', 'name'));
            setTrackers(sortedTrackers);
        }
    }, [data]);

    const initialValues = {
        trackers: defaultTrackers,
    }

    const handleSelectAll = (setFieldValue) => {
        setAllTrackersSelected(() => {
            let _tempSelected = [...defaultTrackers];
            if (!allTrackersSelected) {
                _tempSelected = [...trackers];
            }
            setFieldValue('trackers', _tempSelected);
            return !allTrackersSelected;
        });
    }
    
    // handleChange of formik
    const handleValueChanges = (e, setFieldValue) => {
        const selectedInsights = e.target.value;
        // return when select all is clicked.
        if(selectedInsights.includes(null)) {
            return;
        }
        if (data.trackers && data.trackers.length > 0) {
            for (const tracker of data.trackers) {
                if (tracker.name.includes("-detect")) {
                    if (!selectedInsights.some(item => tracker?.name === item?.name)) {
                        selectedInsights.push(tracker);
                    }
                }
            }
        }
        const selectedValues = _.uniqBy(selectedInsights.filter((item) => item != null), 'name');
        if (data.trackers.length === selectedValues.length && !allTrackersSelected) {
            setAllTrackersSelected(true)
        } else if (allTrackersSelected) {
            setAllTrackersSelected(false)
        }
        setFieldValue('trackers', selectedValues);
    }

    /**
     * Submit Method to create a campaign
     */
    const submitForm = async (values, { setSubmitting }) => {
        const campaignsExist = allCampaigns.some(s => s.name.trim().toLowerCase() === data.campaignName.trim().toLowerCase());
        if(campaignsExist) {
            showSnackbar(`Campaign "${data.campaignName}" already exists.`, "error");
            setSubmitting(false);
            return;
        }
        setSubmitting(true);
        try {
            data.trackers = [...values.trackers];
            const createdCompany = await Utils.genericMutation("createCampaign", {
                name: data.campaignName.trim(),
                startdate: data.startDate,
                enddate: data.endDate,
                type: data.type,
                direction: data.direction,
                trackers: JSON.stringify(data.trackers),
                groups: currentNonAdminGroup,
                pinned: false,
                active: true,
                companyCampaignsId: data.companyId
            });
            showSnackbar(CAMPAIGN_CREATED_SUCCESS);
            setSubmitting(false);
            onClose(createdCompany);
        } catch (error) {
            setSubmitting(false);
            showSnackbar(CAMPAIGN_CREATED_FAILED, "error")
        }
    }

    const handleClose = (event, reason) => {
        // Prevent closing the dialog on backdrop click or escape key press
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        event.stopPropagation();
        onClose(null);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="draggable-dialog-title"
            scroll="paper"
            disableEscapeKeyDown={true}
            fullWidth={true}
            maxWidth="sm"
            PaperComponent={DraggableDialog}
            onClick={(e) => e.stopPropagation()}
        >
            <DialogTitle
                id="draggable-dialog-title"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                gap={1}
            >
                New Campaign
                <IconButton
                    className="draggable-cancel"
                    size="small"
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        color: theme.palette.grey[400],
                        marginRight: "-.5rem",
                    })}
                >
                    <FeatherIcon icon="x" />
                </IconButton>
            </DialogTitle>
            <Formik
                enableReinitialize
                validationSchema={AddCampaignTrackerSchema}
                initialValues={initialValues}
                onSubmit={(values, actions) => {
                    submitForm(values, actions);
                }}
            >
                {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    setFieldTouched,
                    values,
                    errors,
                    touched,
                    isSubmitting,
                }) => (
                    <Box
                        component="form"
                        display="flex"
                        flexDirection="column"
                        flexGrow={1}
                        overflow="auto"
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        <DialogContent dividers sx={{paddingY: 3}}>
                            <CreateCampaignStepper current={2} />
                            <FormControl fullWidth sx={{ marginTop: 3}}
                            error={touched.trackers && !!errors.trackers} required>
                                <InputLabel id="demo-simple-select-label">
                                    Add Insight Sets
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Add Insight Sets"
                                    name={"trackers"}
                                    value={values.trackers}
                                    onChange={(e) => handleValueChanges(e, setFieldValue)}
                                    onBlur={handleBlur}
                                    error={touched.trackers && !!errors.trackers}
                                    helperText={touched.trackers ? errors.trackers : ''}
                                    multiple
                                    minrows={3}
                                    multiline
                                    renderValue={(selected) => {
                                        return selected.map((j) => <div>{(j?.name)}</div>);
                                    }}
                                    MenuProps={{ width: "100%" }}
                                    disabled={isSubmitting}
                                >
                                    <MenuItem value={null} key={'select-all'}
                                        id={"item-select-all-insights"}
                                        divider={true}
                                        selected={allTrackersSelected}
                                        onClick={() => handleSelectAll(setFieldValue)}>
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "row",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Checkbox
                                                checked={allTrackersSelected}
                                                indeterminate={values.trackers.length > 0 && values.trackers.length < trackers.length}
                                            />
                                            <ListItemText primary={"Select All"} />
                                        </div>
                                    </MenuItem>
                                    {trackers.map((z, zid) => (
                                        <MenuItem
                                            selected={values.trackers.some((l) => l?.name === z.name)}
                                            value={z}
                                            key={zid}
                                            name={z.name}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Checkbox checked={values.trackers.some((l) => l?.name === z.name)} />
                                                <ListItemText primary={z.name} />
                                            </div>
                                        </MenuItem>
                                    ))}
                                </Select>
                                {(touched.trackers && !!errors.trackers) ? (
                                        <FormHelperText error={true}>{errors.trackers}</FormHelperText>
                                ) : ''}
                            </FormControl>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <CircularProgress color="inherit" size={24} /> : "Create"}
                            </Button>
                        </DialogActions>
                    </Box>
                )}
            </Formik>
        </Dialog>
    );
}

export default CreateCampaignStepTwo;